import React, { useContext, useEffect, useRef, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import './citation.scss';
import { AppContext } from '@/context/AppContext';
import ChevronIcon from '@/assets/icon-cheveron.svg?react';
import WebIcon from '@/assets/icon-web.svg?react';
import KbIcon from '@/assets/icon-knowledge-base.svg?react';
import Favicon from './Favicon';

interface CitationBadgeProps {
  group: CitationGroup;
}

const getDomain = (url?: string) => {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const HOVER_HIDE_DELAY_MS = 500;
const POPOVER_MAX_HEIGHT = 300;
const PLACEMENT_GAP = 8;

const CitationBadge = ({ group }: CitationBadgeProps) => {
  const { citationType, references } = group || ({} as CitationGroup);
  const [show, setShow] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top');

  const { isTouchDevice } = useContext(AppContext) as any;
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const updatePlacement = () => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceAbove = rect.top;
    setPlacement(spaceAbove >= POPOVER_MAX_HEIGHT + PLACEMENT_GAP ? 'top' : 'bottom');
  };

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const scheduleHide = () => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      setShow(false);
      hideTimeoutRef.current = null;
    }, HOVER_HIDE_DELAY_MS);
  };

  useEffect(() => clearHideTimeout, []);

  useEffect(() => {
    if (!show || !isTouchDevice) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShow(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [show, isTouchDevice]);

  if (!references || references.length === 0) return null;

  const first = references[0];
  const extraCount = references.length - 1;
  const badgeLabel = citationType === 'web' ? getDomain(first.url) || first.title : first.title;

  const handleMouseEnter = () => {
    if (!isTouchDevice) {
      clearHideTimeout();
      if (!show) setActiveIndex(0);
      updatePlacement();
      setShow(true);
    }
  };
  const handleMouseLeave = () => {
    if (!isTouchDevice) scheduleHide();
  };
  const handlePopoverMouseEnter = () => {
    if (!isTouchDevice) clearHideTimeout();
  };
  const handlePopoverMouseLeave = () => {
    if (!isTouchDevice) {
      clearHideTimeout();
      setShow(false);
    }
  };
  const handleClick = () => {
    if (isTouchDevice) {
      if (!show) updatePlacement();
      setShow((s) => !s);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeIndex < references.length - 1) {
      setActiveIndex((i) => i + 1);
    }
  };

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeIndex > 0) {
      setActiveIndex((i) => i - 1);
    }
  };

  const renderContent = () => {
    if (citationType === 'web') {
      const active = references[activeIndex] || first;

      return (
        <div className="citation-popover-content citation-popover-web d-flex flex-column p-3">
          <a
            href={active.url}
            target="_blank"
            rel="noopener noreferrer"
            className="citation-web-link"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="citation-web-url d-flex align-items-center">
              {active.favIcon ? (
                <Favicon src={active.favIcon} className="citation-web-icon" />
              ) : (
                <WebIcon />
              )}
              <span>{getDomain(active.url)}</span>
            </div>

            <div className="citation-web-title">{active.title}</div>

            {active.cited_text && (
              <div className="citation-web-description" title={active.cited_text}>
                {active.cited_text}
              </div>
            )}
          </a>

          {references.length > 1 && (
            <>
              <div className="citation-web-divider" />

              <div className="citation-web-pagination d-flex align-items-center justify-content-start mt-1">
                <button
                  type="button"
                  className="citation-nav-btn d-flex align-items-center justify-content-center"
                  onClick={handlePrev}
                  aria-label="Previous citation"
                  disabled={activeIndex === 0}
                >
                  <ChevronIcon />
                </button>

                <span className="citation-page-indicator">
                  {activeIndex + 1}/{references.length}
                </span>

                <button
                  type="button"
                  className="citation-nav-btn d-flex align-items-center justify-content-center"
                  onClick={handleNext}
                  aria-label="Next citation"
                  disabled={activeIndex === references.length - 1}
                >
                  <ChevronIcon />
                </button>
              </div>
            </>
          )}
        </div>
      );
    }

    if (citationType === 'kb') {
      return (
        <div className="citation-popover-content citation-popover-kb d-flex flex-column p-3">
          {references.map((ref, i) => (
            <div
              key={ref.id || `${ref.title}-${i}`}
              className="citation-list-item d-flex align-items-center"
              onClick={() => {
                if (ref.url) {
                  window.open(ref.url, '_blank', 'noopener,noreferrer');
                }
              }}
              role={ref.url ? 'link' : undefined}
            >
              <div className="citation-web-url d-flex align-items-center">
                <KbIcon />
              </div>
              <span className={ref.url ? 'citation-list-item-link' : ''}>{ref.title}</span>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="citation-popover-content citation-popover-list d-flex flex-column p-3 gap-1">
        {references.map((ref, i) => (
          <div
            key={ref.id || `${ref.title}-${i}`}
            className="citation-list-item citation-list-item-plain d-flex align-items-center"
          >
            <span>{ref.title}</span>
          </div>
        ))}
      </div>
    );
  };

  const popover = (
    <Popover
      id={`citation-popover-${group.index}`}
      className={`citation-popover citation-popover-${citationType}`}
      onMouseEnter={handlePopoverMouseEnter}
      onMouseLeave={handlePopoverMouseLeave}
    >
      {renderContent()}
    </Popover>
  );

  return (
    <OverlayTrigger placement={placement} show={show} overlay={popover}>
      <span
        ref={wrapperRef}
        className={`citation-badge citation-badge--${citationType} d-inline-flex align-items-center align-middle mx-1${
          show ? ' citation-badge--open' : ''
        }`}
        role="button"
        tabIndex={0}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!show) updatePlacement();
            setShow((s) => !s);
          }
        }}
      >
        <span className="citation-badge-label">{badgeLabel}</span>
        {extraCount > 0 && <span className="citation-badge-extra">+{extraCount}</span>}
      </span>
    </OverlayTrigger>
  );
};

export default CitationBadge;
